import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  RiArrowDownSLine,
  RiArticleLine,
  RiCalendarEventLine,
  RiCloseLine,
  RiMenuLine,
} from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";

import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "../shared/Logo";
import MobileMenu from "./MobileMenu";

import { NAV_OPTIONS } from "../../utils/optionUtils";
import { MAIN_ROUTES } from "../../utils/routes";

const Nav = () => {
  const location = useLocation();
  const { t } = useTranslation("navigation");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [desktopDropdown, setDesktopDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const desktopNavRef = useRef(null);

  const navigationOptions = useMemo(() => Array.from(NAV_OPTIONS), []);
  const resourceIcons = useMemo(
    () => ({
      article: RiArticleLine,
      calendar: RiCalendarEventLine,
    }),
    [],
  );

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setDesktopDropdown(null);
    setMobileDropdown(null);
  }, [location]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!desktopNavRef.current?.contains(event.target)) {
        setDesktopDropdown(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setDesktopDropdown(null);
        setMobileDropdown(null);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const isActiveLink = (path) => {
    if (path.startsWith("http")) {
      return false;
    }

    if (path.includes("#")) {
      const [pathname, hash] = path.split("#");
      return location.pathname === pathname && location.hash === `#${hash}`;
    }

    return location.pathname === path;
  };

  const renderNavLink = (value, className, onClick) => {
    const label = t(value.LABEL);

    if (value.EXTERNAL) {
      return (
        <a
          className={className}
          href={value.PATH}
          onClick={onClick}
          rel="noopener noreferrer"
          target="_blank"
        >
          {label}
        </a>
      );
    }

    return (
      <Link className={className} onClick={onClick} to={value.PATH}>
        {label}
      </Link>
    );
  };

  const primaryNavigationOptions = navigationOptions.filter(
    ([key]) => key !== "CONTACT",
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/20 bg-white/70 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-8xl items-center px-4 sm:px-6 lg:px-8">
        <Logo />

        {/* Desktop: Left Aligned Items */}
        <div
          className="ml-8 hidden min-w-0 flex-1 items-center justify-between lg:flex"
          ref={desktopNavRef}
        >
          <nav className="flex min-w-0 items-center gap-2">
            {primaryNavigationOptions.map(([key, value]) => {
              const hasItems = value.ITEMS && value.ITEMS.length > 0;
              const isOpen = desktopDropdown === key;
              const linkClasses = `px-4 py-2 text-sm font-medium transition ${
                isActiveLink(value.PATH) || isOpen
                  ? "text-sky-600"
                  : "text-slate-700  hover:text-sky-600"
              }`;

              if (!hasItems) {
                return (
                  <div key={key} className="flex h-full items-center">
                    {renderNavLink(value, linkClasses)}
                  </div>
                );
              }

              return (
                <div
                  className="relative"
                  key={key}
                  onMouseEnter={() => setDesktopDropdown(key)}
                  onMouseLeave={() => setDesktopDropdown(null)}
                >
                  <button
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    className={`${linkClasses} flex items-center gap-1`}
                    onClick={() =>
                      setDesktopDropdown((current) =>
                        current === key ? null : key,
                      )
                    }
                    type="button"
                  >
                    <span>{t(value.LABEL)}</span>
                    <RiArrowDownSLine
                      className={`text-lg transition ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isOpen ? (
                    <div className="absolute left-0 top-[calc(100%+0rem)] w-72 rounded-2xl border border-slate-200 bg-white py-3 shadow-xl">
                      {value.ITEMS.map(([itemKey, item]) => (
                        <Link
                          className="flex items-start gap-4 px-6 py-3 transition hover:bg-slate-50"
                          key={itemKey}
                          to={item.PATH}
                        >
                          {resourceIcons[item.ICON] ? (
                            React.createElement(resourceIcons[item.ICON], {
                              className: "mt-0.5 shrink-0 text-lg text-sky-500",
                            })
                          ) : null}
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              {t(item.LABEL)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {t(item.DESCRIPTION)}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>

          <div className="ml-6 flex shrink-0 items-center gap-3">
            <Link
              className="inline-flex items-center justify-center rounded-full bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600"
              to={MAIN_ROUTES.get("CONTACT").PATH}
            >
              {t(MAIN_ROUTES.get("CONTACT").LABEL)}
            </Link>
            <LanguageSwitcher />
            <div className="h-6 w-px bg-slate-300" />
            <a
              className="px-2 text-sm font-medium text-sky-600 transition hover:text-sky-700"
              href="https://www.comp.nus.edu.sg/~dbsystem/"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t("dbSystem")}
            </a>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3 lg:hidden">
          <Link
            className="inline-flex items-center justify-center rounded-full bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600"
            to={MAIN_ROUTES.get("CONTACT").PATH}
          >
            {t(MAIN_ROUTES.get("CONTACT").LABEL)}
          </Link>

          <button
            aria-expanded={isMobileMenuOpen}
            aria-label={t("toggleMenu")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-slate-700 transition hover:border-sky-200 hover:text-sky-600 lg:hidden"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            type="button"
          >
            {isMobileMenuOpen ? (
              <RiCloseLine className="text-xl" />
            ) : (
              <RiMenuLine className="text-xl" />
            )}
          </button>
        </div>
      </div>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        mobileDropdown={mobileDropdown}
        navigationOptions={primaryNavigationOptions}
        onClose={() => setIsMobileMenuOpen(false)}
        onToggleDropdown={(key) =>
          setMobileDropdown((current) => (current === key ? null : key))
        }
        resourceIcons={resourceIcons}
        renderNavLink={renderNavLink}
      />
    </header>
  );
};

export default Nav;
